import { useEffect, useState, useMemo } from "react";
import { isCapabilityEnabled } from "../../config/capabilities";
import {
  getDoctorByUserId,
  getSmartChartByPatient,
  getTranscribeJobsByPatient,
  getSummarizeJobsByPatient,
  getTraceByPatient,
} from "./patientApi";

export function usePatientSupportData(patient, refreshKey = 0) {
  const patientId = patient?.patient_id;
  const userId = patient?.user_id;
  const smartChartEnabled = isCapabilityEnabled("smartChart");

  const [doctor, setDoctor] = useState(null);
  const [smartChart, setSmartChart] = useState(null);
  const [transcription, setTranscription] = useState(null);
  const [summarization, setSummarization] = useState(null);
  const [trace, setTrace] = useState(null);

  const [loading, setLoading] = useState({
    doctor: false,
    smartChart: false,
    transcription: false,
    summarization: false,
    trace: false,
  });

  const [fetchErrors, setFetchErrors] = useState({
    doctor: null,
    smartChart: null,
    transcription: null,
    summarization: null,
    trace: null,
  });

  useEffect(() => {
    if (!patientId) {
      setDoctor(null);
      setSmartChart(null);
      setTranscription(null);
      setSummarization(null);
      setTrace(null);
      setFetchErrors({
        doctor: null,
        smartChart: null,
        transcription: null,
        summarization: null,
        trace: null,
      });
      setLoading({
        doctor: false,
        smartChart: false,
        transcription: false,
        summarization: false,
        trace: false,
      });
      return;
    }

    let cancelled = false;

    const applyResult = (key, result, setData) => {
      if (result?.error) {
        setFetchErrors((prev) => ({ ...prev, [key]: result.error }));
        setData(null);
      } else {
        setFetchErrors((prev) => ({ ...prev, [key]: null }));
        setData(result);
      }
    };

    const fetchAll = async () => {
      setLoading({
        doctor: Boolean(userId),
        smartChart: smartChartEnabled,
        transcription: true,
        summarization: true,
        trace: true,
      });
      setDoctor(null);
      setSmartChart(null);
      setTranscription(null);
      setSummarization(null);
      setTrace(null);
      setFetchErrors({
        doctor: userId ? null : "No doctor user ID on patient",
        smartChart: null,
        transcription: null,
        summarization: null,
        trace: null,
      });

      const [
        doctorResult,
        smartChartResult,
        transcribeResult,
        summarizeResult,
        traceResult,
      ] =
        await Promise.all([
          userId ? getDoctorByUserId(userId) : Promise.resolve({ error: "No doctor user ID" }),
          smartChartEnabled
            ? getSmartChartByPatient(patientId)
            : Promise.resolve(null),
          getTranscribeJobsByPatient(patientId),
          getSummarizeJobsByPatient(patientId),
          getTraceByPatient(patientId),
        ]);

      if (cancelled) return;

      applyResult("doctor", userId ? doctorResult : { error: "No doctor user ID" }, setDoctor);
      if (smartChartEnabled) {
        applyResult("smartChart", smartChartResult, setSmartChart);
      }
      applyResult("transcription", transcribeResult, setTranscription);
      applyResult("summarization", summarizeResult, setSummarization);
      applyResult("trace", traceResult, setTrace);

      setLoading({
        doctor: false,
        smartChart: false,
        transcription: false,
        summarization: false,
        trace: false,
      });
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [patientId, userId, refreshKey, smartChartEnabled]);

  const isLoading =
    loading.doctor ||
    loading.smartChart ||
    loading.transcription ||
    loading.summarization ||
    loading.trace;

  const validationContext = useMemo(
    () => ({
      patient: patient?.error ? null : patient,
      doctor,
      smartChart,
      transcription,
      summarization,
      trace,
      fetchErrors,
      isLoading,
    }),
    [patient, doctor, smartChart, transcription, summarization, trace, fetchErrors, isLoading]
  );

  return {
    doctor,
    smartChart,
    transcription,
    summarization,
    trace,
    loading,
    fetchErrors,
    isLoading,
    validationContext,
  };
}
